import { Alert, Fade, Snackbar, SxProps, Theme } from '@mui/material';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

export enum Severity {
    success = 'success',
    error = 'error',
    warning = 'warning',
    info = 'info',
}

export interface Toast {
    message: string;
    severity: Severity;
    open: boolean;
    options?: ToastOptions;
}

type ToastOptions = {
    sx?: SxProps<Theme>;
    alertSx?: SxProps<Theme>;
    verticalPosition?: 'bottom' | 'top';
};

interface ToastContextValue {
    showToast: (message: string, severity: Severity, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
    const [toast, setToast] = useState<Toast>({
        message: '',
        severity: Severity.info,
        open: false,
        options: {
            sx: {},
            alertSx: {},
            verticalPosition: 'bottom',
        },
    });

    const showToast = (message: string, severity: Severity, options?: ToastOptions) => {
        setToast({
            message,
            severity,
            open: true,
            options: {
                sx: options?.sx,
                alertSx: options?.alertSx,
                verticalPosition: options?.verticalPosition ?? 'bottom',
            },
        });
    };

    const setOpen = (open: boolean) => setToast((prev) => ({ ...prev, open }));

    useEffect(() => {
        if (toast.open) {
            setTimeout(() => {
                setOpen(false);
            }, 2000);
        }
    }, [toast]);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}

            <Snackbar
                anchorOrigin={{
                    vertical: toast.options?.verticalPosition ?? 'bottom',
                    horizontal: 'center',
                }}
                open={toast.open}
                autoHideDuration={3000}
                TransitionComponent={Fade}
                onClose={() => setOpen(false)}
                sx={toast.options?.sx}
            >
                <Alert onClose={() => setOpen(false)} severity={toast.severity} sx={toast.options?.alertSx}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error();
    }
    return context.showToast;
};
